import { memo, useEffect, useMemo, useRef } from 'react';

interface CircularTimerProps {
  width: number;
  height: number;
  strokeWidth?: number;
  strokeColor?: string;
  ellipseBorderColor?: string;
  /** SDK scan progress in [0, 1]. */
  progress?: number;
  duration?: number;
  isActive: boolean;
}

function createEllipsePath(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): string {
  return `M ${cx + rx}, ${cy} a ${rx},${ry} 0 1,0 ${-2 * rx},0 a ${rx},${ry} 0 1,0 ${2 * rx},0`;
}

function ellipsePerimeter(rx: number, ry: number): number {
  return Math.PI * (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)));
}

function ellipseLabelKeyframes(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  samples = 48,
): Keyframe[] {
  const frames: Keyframe[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const theta = 2 * Math.PI * t;
    frames.push({
      transform: `translate(${cx + rx * Math.cos(theta) - 20}px, ${cy + ry * Math.sin(theta) - 20}px)`,
      offset: t,
    });
  }
  return frames;
}

function CircularTimerImpl({
  width,
  height,
  strokeWidth = 4,
  ellipseBorderColor = '#1A80D9',
  strokeColor = 'white',
  progress = 0,
  duration = 70,
  isActive,
}: CircularTimerProps) {
  const cx = width / 2;
  const cy = height / 2;
  const rx = (width - strokeWidth) / 2;
  const ry = (height - strokeWidth) / 2;
  const perimeter = useMemo(() => ellipsePerimeter(rx, ry), [rx, ry]);
  const path = useMemo(
    () => createEllipsePath(cx, cy, rx, ry),
    [cx, cy, rx, ry],
  );

  const borderPathRef = useRef<SVGPathElement | null>(null);
  const progressPathRef = useRef<SVGPathElement | null>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);
  const labelTextRef = useRef<HTMLSpanElement | null>(null);
  const ringAnimRef = useRef<Animation | null>(null);
  const labelAnimRef = useRef<Animation | null>(null);
  const labelTickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationRef = useRef(duration);
  durationRef.current = duration;
  const geomRef = useRef({ cx, cy, rx, ry, perimeter, path });
  geomRef.current = { cx, cy, rx, ry, perimeter, path };
  const startedRef = useRef(false);

  const setLabelSeconds = (p: number) => {
    const textEl = labelTextRef.current;
    if (!textEl) return;
    const d = durationRef.current;
    textEl.textContent = String(Math.min(d, Math.max(0, Math.round(p * d))));
  };

  /** Forward-only sync — never scrub backward (that caused jump-backs). */
  const syncForward = (p: number) => {
    const clamped = Math.min(1, Math.max(0, p));
    const targetMs = clamped * durationRef.current * 1000;
    const ring = ringAnimRef.current;
    const label = labelAnimRef.current;
    if (!ring || !label) return;

    const currentMs = Number(ring.currentTime ?? 0);
    if (targetMs > currentMs + 16) {
      ring.currentTime = targetMs;
      label.currentTime = targetMs;
    }
    if (ring.playState !== 'finished') ring.play();
    if (label.playState !== 'finished') label.play();
    setLabelSeconds(clamped);
  };

  useEffect(() => {
    const el = borderPathRef.current;
    if (el) el.setAttribute('stroke', ellipseBorderColor);
  }, [ellipseBorderColor]);

  useEffect(() => {
    const pathEl = progressPathRef.current;
    const labelEl = labelRef.current;
    const textEl = labelTextRef.current;

    const cancelAll = () => {
      ringAnimRef.current?.cancel();
      labelAnimRef.current?.cancel();
      ringAnimRef.current = null;
      labelAnimRef.current = null;
      startedRef.current = false;
      if (labelTickRef.current != null) {
        clearInterval(labelTickRef.current);
        labelTickRef.current = null;
      }
      if (pathEl) pathEl.style.strokeDashoffset = '0';
      if (textEl) textEl.textContent = '0';
    };

    if (!isActive || !pathEl || !labelEl) {
      cancelAll();
      return;
    }

    const g = geomRef.current;
    pathEl.setAttribute('d', g.path);
    borderPathRef.current?.setAttribute('d', g.path);
    pathEl.style.strokeDasharray = String(g.perimeter);
    pathEl.style.strokeDashoffset = '0';
    if (textEl) textEl.textContent = '0';

    return () => {
      cancelAll();
    };
  }, [isActive, duration]);

  useEffect(() => {
    if (!isActive) return;

    const pathEl = progressPathRef.current;
    const labelEl = labelRef.current;
    if (!pathEl || !labelEl) return;

    const sdk = Math.min(1, Math.max(0, progress));

    if (!startedRef.current) {
      // Start with the first real SDK tick so we don't race ahead of recording.
      if (sdk <= 0) return;

      const g = geomRef.current;
      const durationMs = durationRef.current * 1000;

      ringAnimRef.current?.cancel();
      labelAnimRef.current?.cancel();

      ringAnimRef.current = pathEl.animate(
        [
          { strokeDashoffset: 0 },
          { strokeDashoffset: g.perimeter },
        ],
        { duration: durationMs, easing: 'linear', fill: 'forwards' },
      );
      labelAnimRef.current = labelEl.animate(
        ellipseLabelKeyframes(g.cx, g.cy, g.rx, g.ry),
        { duration: durationMs, easing: 'linear', fill: 'forwards' },
      );

      startedRef.current = true;
      ringAnimRef.current.currentTime = sdk * durationMs;
      labelAnimRef.current.currentTime = sdk * durationMs;
      setLabelSeconds(sdk);

      if (labelTickRef.current != null) clearInterval(labelTickRef.current);
      labelTickRef.current = setInterval(() => {
        const ring = ringAnimRef.current;
        if (!ring) return;
        const t = Number(ring.currentTime ?? 0);
        setLabelSeconds(Math.min(1, t / (durationRef.current * 1000)));
      }, 250);
      return;
    }

    syncForward(sdk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, progress, duration]);

  if (width <= 0 || height <= 0) return null;

  return (
    <div
      className='absolute overflow-visible pointer-events-none'
      style={{ width, height }}
      aria-hidden
    >
      <svg
        width={width}
        height={height}
        className='absolute inset-0 overflow-visible'
        style={{ overflow: 'visible' }}
      >
        <path
          ref={borderPathRef}
          d={path}
          stroke={ellipseBorderColor}
          strokeWidth={strokeWidth}
          fill='none'
        />
        <path
          ref={progressPathRef}
          d={path}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fill='none'
          strokeLinecap='round'
          style={{ strokeDasharray: perimeter, strokeDashoffset: 0 }}
        />
      </svg>
      <div
        ref={labelRef}
        className='absolute z-10 flex items-center justify-center bg-white rounded-full shadow-sm'
        style={{
          width: 40,
          height: 40,
          left: 0,
          top: 0,
          transform: `translate(${cx + rx - 20}px, ${cy - 20}px)`,
          willChange: 'transform',
        }}
      >
        <span ref={labelTextRef} className='text-xs text-black tabular-nums'>
          0
        </span>
      </div>
    </div>
  );
}

function timerPropsEqual(
  prev: CircularTimerProps,
  next: CircularTimerProps,
): boolean {
  return (
    prev.width === next.width &&
    prev.height === next.height &&
    prev.strokeWidth === next.strokeWidth &&
    prev.strokeColor === next.strokeColor &&
    prev.ellipseBorderColor === next.ellipseBorderColor &&
    prev.duration === next.duration &&
    prev.isActive === next.isActive &&
    prev.progress === next.progress
  );
}

export const CircularTimer = memo(CircularTimerImpl, timerPropsEqual);
